import { Check } from "lucide-react";
import { useState } from "react";
import { Badge } from "../ui/Badge";
import { setFranchiseAnalyzed } from "../../interfaces/establishments";

/** Past this many days a network is due for another look; past STALE_DAYS it is overdue. */
export const FRESH_DAYS = 30;
export const STALE_DAYS = 90;

export function daysSince(date: string | null | undefined): number | null {
    if (!date) return null;
    return Math.floor((Date.now() - new Date(date).getTime()) / 86_400_000);
}

function ageLabel(days: number): string {
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return "Hier";
    if (days < 60) return `Il y a ${days} j`;
    const months = Math.floor(days / 30);
    if (months < 12) return `Il y a ${months} mois`;
    const years = Math.floor(days / 365);
    return `Il y a ${years} an${years > 1 ? "s" : ""}`;
}

/** How long since the last analysis, coloured by how overdue it is. */
export function AnalysisAge({ date }: { date: string | null | undefined }) {
    const days = daysSince(date);
    if (days === null) return <Badge tone="neutral">Jamais analysée</Badge>;
    const tone = days < FRESH_DAYS ? "success" : days < STALE_DAYS ? "warning" : "danger";
    return (
        <Badge tone={tone} title={new Date(date!).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {ageLabel(days)}
        </Badge>
    );
}

/**
 * The tick that stamps today as the franchise's last analysis. Ticked today → clicking again
 * undoes it (clears the date), so a misclick costs nothing. Older dates are simply refreshed.
 */
export function AnalysisCheckButton({
    franchiseId,
    date,
    onChange,
}: {
    franchiseId: string;
    date: string | null | undefined;
    onChange: (lastAnalyzedAt: string | null) => void;
}) {
    const [saving, setSaving] = useState(false);
    const doneToday = daysSince(date) === 0;

    const toggle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setSaving(true);
        try {
            const res = await setFranchiseAnalyzed(franchiseId, !doneToday);
            onChange(res.lastAnalyzedAt);
        } finally {
            setSaving(false);
        }
    };

    return (
        <button
            onClick={toggle}
            disabled={saving}
            aria-pressed={doneToday}
            title={doneToday ? "Analysée aujourd'hui — cliquer pour annuler" : "Marquer comme analysée aujourd'hui"}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors duration-150
                disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2
                ${doneToday
                    ? "border-success bg-success text-white hover:bg-success/90"
                    : "border-border-strong bg-surface text-ink-faint hover:border-success hover:text-success"}`}
        >
            <Check className="h-4 w-4" />
        </button>
    );
}
